import { Logger, Metadata } from '@samelogic/steps';
import { Configs, getConfig } from './configs';
import { Options } from './models';
import { WorkflowConfig } from './models';
import { Project } from './projects';
import { ProjectsRepository } from './projects/Repository';
import { SessionManager } from './tracking/sessions';
import { RemotePackageRepository } from './workflows/Steps/Repository';
import { PackageMemoryCache } from './workflows/Steps/Repository/PackageMemoryCache';
import { Context } from './workflows/Context/Context';
import { WorkflowManager } from './workflows/WorkflowManager/WorkflowManager';
import { VERSION } from './constants';
import { EventLogger } from './tracking/EventLogger';

/**
 * Samelogic Client SDK API. The Samelogic class handles the creation of a client instance.
 *
 * @public
 */
export class Samelogic {
  private readonly config: Configs;
  //#region Composite Root
  private readonly logger: Logger;

  private readonly sessionManager: SessionManager;
  private readonly projectsRepository: ProjectsRepository;
  private readonly remotePackageRepository: RemotePackageRepository;
  private readonly packageMemoryCache: PackageMemoryCache;

  /**
   * The current version of the library
   */
  public readonly version = VERSION;
  //#endregion

  private workflowManagers = new Map<string, WorkflowManager>();

  /**
   * Samelogic Client SDK API - instance constructor. The Samelogic class handles the creation and configuration
   * of a client instance.
   *
   * @param options - the configuration options for the client instance.
   *
   * @public
   */
  constructor(private readonly options: Options) {
    this.config = getConfig(options);

    // Temporary bind this to console for now
    this.logger = console;

    this.sessionManager = new SessionManager(this.options.envId, options.user);
    this.projectsRepository = new ProjectsRepository(this.config, this.sessionManager);
    this.remotePackageRepository = new RemotePackageRepository();
    this.packageMemoryCache = new PackageMemoryCache(this.remotePackageRepository);
  }

  /**
   * Initializes the client instance. This will start the client to begin listening for triggers and executing worklows.
   *
   * @public
   */
  public async init(): Promise<void> {
    let project: Project;
    if (this.options.workflows) {
      project = {
        envId: this.options.envId,
        workflows: this.options.workflows
      };
    } else {
      project = await this.projectsRepository.getClientProjectByEnvironmentId(this.options.envId);
    }

    if (project.workflows.length < 1) {
      this.logger.info('No workflows loaded');
    }

    this.initSession(project);
    await this.hotLoadPackages(project.workflows);

    for (const workflow of project.workflows) {
      const workflowMetadata: Metadata = {
        name: workflow.name
      };

      const context = new Context(this.sessionManager, workflowMetadata, {});

      const eventLogger = new EventLogger(workflow.logEndpoint);
      eventLogger.setProject({
        envId: project.envId,
        projectId: project.projectId,
        orgId: project.orgId
      });

      const manager = new WorkflowManager(
        context,
        workflow,
        this.packageMemoryCache,
        this.logger,
        eventLogger
      );

      this.workflowManagers.set(workflow.name, manager);
      manager.start();
    }
  }

  private initSession(project: Project): void {
    this.sessionManager.setSessionProperties({
      envId: project.envId,
      projId: project.projectId || '',
      orgId: project.orgId || ''
    });
  }

  private async hotLoadPackages(workflows: WorkflowConfig[]): Promise<void> {
    const stepUrls: string[] = [];
    for (const workflow of workflows) {
      for (const step of workflow.steps) {
        stepUrls.push(step.uses);
      }
    }
    return this.packageMemoryCache.initialize(stepUrls);
  }

  /**
   * Creates an instance of the client and then initializes it. This is a static method and is a convenience method
   * for initializing through a single API call from the window object. This allows tools like Google Tag Manager
   * to initialize the client instance without the `new` keyword being available.
   *
   * @public
   */
  public static async init(options: Options): Promise<Samelogic> {
    const samelogic = new Samelogic(options);
    await samelogic.init();

    window.samelogic = samelogic;

    return samelogic;
  }

  /**
   * This method is used to uniquelly identify the user for maintaining an association across sessions.
   *
   * @param identity - this is a unique identifier for the user, such as an email address or a user ID.
   * @param properties - this is a set of key/value pairs that can be used to enrich the user's profile.
   */
  public identify(identity: string, properties?: Record<string, string>): void {
    const sessionIdentity = this.sessionManager.getSessionIdentity();
    sessionIdentity.identity = identity;
    sessionIdentity.properties = properties;
    this.sessionManager.setSessionIdentity(sessionIdentity);

    this.workflowManagers.forEach((x) => x.eventLogger.setSessionIdentity(sessionIdentity));
  }

  /**
   * This method is used to reset the user session and identity. This can be used when the user logs out.
   */
  public resetIdentity(): void {
    this.sessionManager.resetSessionIdentity();
    this.workflowManagers.forEach((x) =>
      x.eventLogger.setSessionIdentity(this.sessionManager.getSessionIdentity())
    );
  }
}
